import React, { useState, useRef } from 'react'
import { CloudArrowUpIcon, XMarkIcon, DocumentIcon } from '@heroicons/react/24/outline'
import { DocumentType } from '../types'
import { useUploadDocument } from '../hooks/useDocuments'
import LoadingSpinner from './LoadingSpinner'

interface DocumentUploadProps {
  caseId: string
  documentType: DocumentType
  onUploadSuccess?: () => void
  disabled?: boolean
}

const documentTypeLabels: Record<DocumentType, string> = {
  [DocumentType.MEDICAL_CERTIFICATE]: 'Medical Certificate of Death',
  [DocumentType.BURIAL_PERMIT]: 'Burial Permit',
  [DocumentType.IDENTIFICATION]: 'Identification Document',
  [DocumentType.CONSENT_FORM]: 'Family Consent Form',
  [DocumentType.OTHER]: 'Other Document',
}

const documentTypeDescriptions: Record<DocumentType, string> = {
  [DocumentType.MEDICAL_CERTIFICATE]: 'Official medical certificate confirming death',
  [DocumentType.BURIAL_PERMIT]: 'Official permit for burial from local authorities',
  [DocumentType.IDENTIFICATION]: 'ID card or passport of the deceased',
  [DocumentType.CONSENT_FORM]: 'Signed consent from next of kin',
  [DocumentType.OTHER]: 'Any other relevant document',
}

function DocumentUpload({ caseId, documentType, onUploadSuccess, disabled = false }: DocumentUploadProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadMutation = useUploadDocument()

  const handleFileSelect = (file: File) => {
    // Check file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB')
      return
    }

    // Check file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
    ]

    if (!allowedTypes.includes(file.type)) {
      alert('File type not supported. Please upload PDF, DOC, DOCX, JPG, PNG, GIF, or WebP files.')
      return
    }

    setSelectedFile(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const files = e.dataTransfer.files
    if (files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    try {
      await uploadMutation.mutateAsync({
        caseId,
        file: selectedFile,
        documentType,
      })

      setSelectedFile(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

      onUploadSuccess?.()
    } catch (error) {
      // Error is handled by the mutation hook
    }
  }

  const clearSelectedFile = () => {
    setSelectedFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className="card">
      <div className="card-body">
        <div className="mb-4">
          <h3 className="text-lg font-medium text-neutral-900">
            {documentTypeLabels[documentType]}
          </h3>
          <p className="text-sm text-neutral-600 mt-1">
            {documentTypeDescriptions[documentType]}
          </p>
        </div>

        {!selectedFile ? (
          <div
            className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              isDragging
                ? 'border-primary-400 bg-primary-50'
                : 'border-neutral-300 hover:border-neutral-400'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => !disabled && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
              onChange={handleFileInputChange}
              disabled={disabled}
            />
            <CloudArrowUpIcon className="mx-auto h-12 w-12 text-neutral-400" />
            <div className="mt-4">
              <p className="text-sm text-neutral-600">
                <span className="font-medium text-primary-600">Click to upload</span> or drag and drop
              </p>
              <p className="text-xs text-neutral-500 mt-1">
                PDF, DOC, DOCX, JPG, PNG, GIF, or WebP up to 10MB
              </p>
            </div>
          </div>
        ) : (
          <div className="border-2 border-primary-200 rounded-lg p-4 bg-primary-50">
            <div className="flex items-start justify-between">
              <div className="flex items-start">
                <DocumentIcon className="h-10 w-10 text-primary-600 mt-1" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-neutral-900">
                    {selectedFile.name}
                  </p>
                  <p className="text-xs text-neutral-500 mt-1">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB • {selectedFile.type}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleUpload}
                  disabled={uploadMutation.isPending}
                  className="btn-primary text-sm px-3 py-1 disabled:opacity-50"
                >
                  {uploadMutation.isPending ? (
                    <div className="flex items-center">
                      <LoadingSpinner size="sm" className="mr-1" />
                      Uploading...
                    </div>
                  ) : (
                    'Upload'
                  )}
                </button>
                <button
                  onClick={clearSelectedFile}
                  disabled={uploadMutation.isPending}
                  className="p-1 text-neutral-400 hover:text-neutral-600 disabled:opacity-50"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {uploadMutation.error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">
              {uploadMutation.error.message || 'Failed to upload document'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default DocumentUpload